import { useState, useEffect } from 'react';
import {
  Radio,
  Plus,
  Trash2,
  Search,
  Loader2,
  Bluetooth,
  X,
  Building2,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import clsx from 'clsx';

interface Tenant {
  id: string;
  name: string;
}

interface AdminBeacon {
  id: string;
  tenantId: string;
  macAddress: string;
  name: string | null;
  type: 'IBEACON' | 'EDDYSTONE';
  lastRssi: number | null;
  lastBattery: number | null;
  lastTemperature: number | null;
  lastHumidity: number | null;
  lastSeen: string | null;
  active: boolean;
  createdAt: string;
  tenant: { id: string; name: string } | null;
  assignedDevice: { id: string; name: string; imei: string } | null;
  assignedDriver: { id: string; name: string } | null;
}

export default function BeaconsAdminPage() {
  const [beacons, setBeacons] = useState<AdminBeacon[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTenantId, setFilterTenantId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminBeacon | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    loadBeacons();
  }, [filterTenantId]);

  const loadTenants = async () => {
    try {
      const res = await adminApi.getTenants({ limit: 1000 });
      const list = res.data?.data || [];
      list.sort((a: Tenant, b: Tenant) => a.name.localeCompare(b.name));
      setTenants(list);
    } catch (error) {
      console.error('Error loading tenants:', error);
    }
  };

  const loadBeacons = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getBeacons({
        tenantId: filterTenantId || undefined,
        search: search || undefined,
        limit: 200,
      });
      setBeacons(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading beacons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadBeacons();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.deleteBeacon(deleteTarget.id);
      setDeleteTarget(null);
      loadBeacons();
    } catch (error) {
      console.error('Error deleting beacon:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-xl">
            <Radio className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Beacons BLE</h1>
            <p className="text-sm text-gray-500">Gestion des Eye Beacons (admin)</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-medium"
        >
          <Plus className="h-4 w-4" />
          Ajouter un beacon
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={filterTenantId}
          onChange={(e) => setFilterTenantId(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-blue-500"
        >
          <option value="">Tous les tenants</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Rechercher par nom ou MAC..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {/* Stats */}
      <p className="text-sm text-gray-500">{total} beacon{total !== 1 ? 's' : ''} au total</p>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : beacons.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Radio className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Aucun beacon</p>
        </div>
      ) : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 font-medium text-gray-500">Nom</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">MAC</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Tenant</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">Vehicule</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden md:table-cell">RSSI</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden md:table-cell">Temp.</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Statut</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500 hidden md:table-cell">Derniere vue</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {beacons.map((b) => (
                <tr key={b.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Bluetooth className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="font-medium text-gray-900">{b.name || 'Sans nom'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{b.macAddress}</td>
                  <td className="px-4 py-3">
                    <span className={clsx(
                      'px-2 py-0.5 text-xs font-medium rounded-full',
                      b.type === 'IBEACON' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    )}>
                      {b.type === 'IBEACON' ? 'iBeacon' : 'Eddystone'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-gray-700 text-xs">{b.tenant?.name || '-'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell text-xs">
                    {b.assignedDevice?.name || '-'}
                  </td>
                  <td className="px-4 py-3 text-center hidden md:table-cell">
                    {b.lastRssi != null ? (
                      <span className={clsx(
                        'text-xs font-mono',
                        b.lastRssi > -60 ? 'text-green-600' : b.lastRssi > -80 ? 'text-yellow-600' : 'text-red-600'
                      )}>
                        {b.lastRssi} dBm
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-center hidden md:table-cell text-xs">
                    {b.lastTemperature != null ? `${b.lastTemperature.toFixed(1)}°C` : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(b)}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell text-center">
                    {b.lastSeen ? formatRelative(b.lastSeen) : 'Jamais'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setDeleteTarget(b)}
                      className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreateBeaconModal
          tenants={tenants}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            loadBeacons();
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="glass-card p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Supprimer le beacon</h3>
            <p className="text-sm text-gray-600 mb-4">
              Supprimer <strong>{deleteTarget.name || deleteTarget.macAddress}</strong> et tout son historique ?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Create Beacon Modal
// ============================================================================

interface TenantDevice {
  id: string;
  name: string;
  imei: string;
  plate?: string;
}

function CreateBeaconModal({
  tenants,
  onClose,
  onCreated,
}: {
  tenants: Tenant[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id || '');
  const [assignedDeviceId, setAssignedDeviceId] = useState('');
  const [devices, setDevices] = useState<TenantDevice[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [macAddress, setMacAddress] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<'IBEACON' | 'EDDYSTONE'>('IBEACON');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) {
      setDevices([]);
      setAssignedDeviceId('');
      return;
    }
    setLoadingDevices(true);
    setAssignedDeviceId('');
    adminApi.getDevices({ tenantId, limit: 500 })
      .then((res) => {
        const list = res.data?.data || [];
        list.sort((a: TenantDevice, b: TenantDevice) => (a.name || '').localeCompare(b.name || ''));
        setDevices(list);
      })
      .catch(() => setDevices([]))
      .finally(() => setLoadingDevices(false));
  }, [tenantId]);

  const handleSubmit = async () => {
    if (!tenantId) {
      setError('Selectionnez un tenant');
      return;
    }
    if (!macAddress || macAddress.trim().length < 5) {
      setError('Adresse MAC invalide');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await adminApi.createBeacon({
        tenantId,
        macAddress: macAddress.trim(),
        name: name.trim() || undefined,
        type,
        assignedDeviceId: assignedDeviceId || undefined,
      });
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la creation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="glass-card p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Ajouter un beacon</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant *</label>
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="">-- Selectionnez --</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehicule (tracker)</label>
            <select
              value={assignedDeviceId}
              onChange={(e) => setAssignedDeviceId(e.target.value)}
              disabled={!tenantId || loadingDevices}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">{loadingDevices ? 'Chargement...' : '-- Aucun (optionnel) --'}</option>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}{d.plate ? ` (${d.plate})` : ''} - {d.imei}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Le tracker qui detectera ce beacon</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adresse MAC *</label>
            <input
              type="text"
              value={macAddress}
              onChange={(e) => setMacAddress(e.target.value)}
              placeholder="AA:BB:CC:DD:EE:FF"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:border-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">L'adresse MAC se trouve sur l'etiquette du beacon</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom (optionnel)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Beacon remorque 1"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'IBEACON' | 'EDDYSTONE')}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="IBEACON">iBeacon</option>
              <option value="EDDYSTONE">Eddystone</option>
            </select>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded-lg">{error}</div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creation...' : 'Creer le beacon'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function getStatusBadge(beacon: AdminBeacon) {
  if (!beacon.active) {
    return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Inactif</span>;
  }
  if (beacon.lastSeen) {
    const diff = Date.now() - new Date(beacon.lastSeen).getTime();
    if (diff < 10 * 60 * 1000) {
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">En ligne</span>;
    }
  }
  return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Hors ligne</span>;
}

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'A l\'instant';
  if (minutes < 60) return `Il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}
